import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

const HeroSection = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userProfile } = useAuth();

  const handleRegistration = () => {
    navigate('/student-registration');
  };

  const handleExplore = () => {
    if (isAuthenticated && userProfile?.role) {
      // Redirect to appropriate dashboard based on user role
      switch (userProfile?.role) {
        case 'parent': navigate('/parent-dashboard-interface');
          break;
        case 'counselor': navigate('/counselor-management-portal');
          break;
        case 'student':
        default:
          navigate('/student-dashboard');
          break;
      }
    } else {
      // Redirect to login if not authenticated
      navigate('/user-login');
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12 md:py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-4 md:mb-6">
              <Icon name="Sparkles" size={20} color="var(--color-primary)" />
              <span className="text-sm md:text-base font-medium text-blue-700">
                Développé par XELALTECH221
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Découvrez Votre Filière Idéale avec l'IA
            </h1>

            <p className="text-base md:text-lg lg:text-xl text-gray-600 mb-6 md:mb-8 leading-relaxed">
              CURSUS Orientation utilise l'intelligence artificielle pour vous guider vers la filière d'études qui correspond parfaitement à vos talents, passions et objectifs de carrière au Sénégal et en Afrique francophone.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                variant="default"
                size="lg"
                iconName="UserPlus"
                iconPosition="left"
                onClick={handleRegistration}
                className="w-full sm:w-auto"
              >
                Commencer Maintenant
              </Button>

              <Button
                variant="outline"
                size="lg"
                iconName="Play"
                iconPosition="left"
                onClick={handleExplore}
                className="w-full sm:w-auto"
              >
                Explorer la Plateforme
              </Button>
            </div>

            <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-6 md:gap-8">
              <div className="flex items-center gap-2">
                <Icon name="Users" size={24} color="var(--color-primary)" />
                <div className="text-left">
                  <div className="text-xl md:text-2xl font-bold text-gray-900">15,000+</div>
                  <div className="text-xs md:text-sm text-gray-600">Étudiants Guidés</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Icon name="School" size={24} color="var(--color-primary)" />
                <div className="text-left">
                  <div className="text-xl md:text-2xl font-bold text-gray-900">200+</div>
                  <div className="text-xs md:text-sm text-gray-600">Écoles Partenaires</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Icon name="Award" size={24} color="var(--color-primary)" />
                <div className="text-left">
                  <div className="text-xl md:text-2xl font-bold text-gray-900">95%</div>
                  <div className="text-xs md:text-sm text-gray-600">Taux de Satisfaction</div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-2xl p-6 md:p-8 border border-gray-100">
              <div className="absolute -top-4 -right-4 w-24 h-24 md:w-32 md:h-32 bg-blue-500 rounded-full opacity-10"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 md:w-40 md:h-40 bg-amber-500 rounded-full opacity-10"></div>

              <div className="relative space-y-4 md:space-y-6">
                <div className="flex items-start gap-3 md:gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <Icon name="MessageSquare" size={20} color="#FFFFFF" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm md:text-base font-semibold text-gray-900 mb-1">
                      Chat IA Personnalisé
                    </div>
                    <div className="text-xs md:text-sm text-gray-600">
                      Discutez avec notre assistant IA pour découvrir vos forces
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4 p-4 bg-green-50 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <Icon name="ClipboardList" size={20} color="#FFFFFF" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm md:text-base font-semibold text-gray-900 mb-1">
                      Quiz Interactifs
                    </div>
                    <div className="text-xs md:text-sm text-gray-600">
                      Évaluez vos aptitudes avec des tests scientifiques
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4 p-4 bg-amber-50 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-amber-500 rounded-full flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} color="#FFFFFF" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm md:text-base font-semibold text-gray-900 mb-1">
                      Recommandations Précises
                    </div>
                    <div className="text-xs md:text-sm text-gray-600">
                      Recevez des suggestions de filières adaptées à votre profil
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;